import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { editablePayload } from "@/pages/wizard/jobUtils";

// Debounced draft autosave. Creates the draft on first meaningful input, then PATCHes changes.
export function useAutosave(job, draftId, onCreated) {
  const [state, setState] = useState("idle");
  const idRef = useRef(draftId);
  const jobRef = useRef(job);
  const timer = useRef(null);
  const creating = useRef(null);
  const lastSent = useRef(null);
  jobRef.current = job;
  if (draftId) idRef.current = draftId;

  const ensureDraft = useCallback(async () => {
    if (idRef.current) return idRef.current;
    if (!creating.current) {
      const j = jobRef.current;
      creating.current = api.post("/jobs", { title: j.title || "", jd_text: j.jd_text || "", practice_area: j.practice_area || "", location: j.location || "",
        law_firm: j.law_firm ?? null, pqe_min: j.pqe_min ?? null, pqe_max: j.pqe_max ?? null }).then(({ data }) => {
        idRef.current = data.id;
        onCreated(data);
        toast.success("Draft saved");
        return data.id;
      });
    }
    return creating.current;
  }, [onCreated]);

  const saveNow = useCallback(async () => {
    clearTimeout(timer.current);
    const id = await ensureDraft();
    const body = editablePayload(jobRef.current);
    const serialized = JSON.stringify(body);
    if (serialized === lastSent.current) return id;
    setState("saving");
    try {
      await api.patch(`/jobs/${id}`, body);
      lastSent.current = serialized;
      setState("saved");
    } catch (e) {
      setState("error");
      toast.error(`Couldn't save the draft: ${errMsg(e)}`);
      throw e;
    }
    return id;
  }, [ensureDraft]);

  useEffect(() => {
    if (!job) return;
    const hasContent = (job.title || "").trim() || (job.jd_text || "").trim();
    if (!idRef.current && !hasContent) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => saveNow().catch(() => {}), 900);
    return () => clearTimeout(timer.current);
  }, [job, saveNow]);

  return { state, saveNow, ensureDraft };
}
